import { put, takeEvery, select } from "redux-saga/effects";
import {
  NMAxiosInst,
  SagaRegistry,
  setAlertAndLoading,
  requestHandler,
  getPriorities,
  getStatuses,
  commonAction,
} from "@neumetric/blueprint-lib/store";
import { DateTimeUtil, Helper } from "@neumetric/blueprint-lib/utility";
import { riskActions } from "./slice";
import { saveAs } from "file-saver";
import {
  getSeverityList,
  getDecisionList,
  getCurrentRisk,
  getRiskStatusList,
  isRiskCompleted,
} from "./helper";

const PER_PAGE = process.env.REACT_APP_RISKS_PER_PAGE || 11;

const mapRisks = (iRisk, iOptions) => {
  const {
    decisionList,
    severityList,
    doPreproccess,
    priorities,
    statuses,
    riskStatusList,
  } = iOptions;
  const risk = { ...iRisk, rating: {} };
  const risk_severity =
    severityList.find((s) => s.id === risk.severity) || null;
  const decision = decisionList.find((d) => d.id === risk.decision) || null;
  const risk_status =
    riskStatusList.find((s) => s.id === risk.risk_status) || null;
  risk.impact = Number(risk.impact);
  risk.score =
    risk.residual_score !== null ? risk.residual_score : Number(risk.score);
  risk.probability = Number(risk.probability);
  risk.status = risk.status || "draft";
  const ratingFields = [
    "confidentiality",
    "integrity",
    "availability",
    "security",
    "privacy",
  ];
  ratingFields.forEach((field) => {
    risk.rating[field] = Number(risk[field]) > -1 ? risk[field] : -1;
  });
  if (risk_severity) {
    risk.risk_severity_item = { ...risk_severity };
  }
  if (decision) {
    risk.decision_item = {
      label: decision.label,
      id: decision.dotIconName || decision.id,
    };
  }
  if (risk_status) {
    risk.risk_status_item = {
      label: risk_status.label,
      id: risk_status.dotIconName || risk_status.id,
    };
  }

  if (Array.isArray(risk.risk_responders)) {
    const responders = [];

    (risk.risk_responders || []).forEach((item) => {
      // if already normalized, keep it
      if (item?.id && item?.label && !item?.responder && !item?.ent_usr) {
        responders.push(item);
        return;
      }
      const userRaw = item?.responder || item?.ent_usr;
      if (!userRaw) return;

      const updated = Helper.getUserObject(userRaw);
      if (!updated?.id) return;

      responders.push({
        id: updated.id,
        label: updated._listname,
        ent_usr: updated,
      });
    });
    risk.risk_responders = responders;
  }
  if (doPreproccess) {
    risk.creator = Helper.getUserObject(risk.creator);
    if (risk.reviewer) {
      risk.reviewer = Helper.getUserObject(risk.reviewer);
    }
    if (risk.accepter) {
      risk.accepter = Helper.getUserObject(risk.accepter);
    }

    if (Array.isArray(risk.tasks)) {
      risk.tasks = risk.tasks.map((task) => {
        task.priority_item = priorities.find(
          (priority) =>
            priority.id.toLowerCase() === task.priority.toLowerCase(),
        );
        task.status_item = statuses.find(
          (status) => status.id.toLowerCase() === task.status.toLowerCase(),
        );
        const responders = [];
        (task.task_responders || []).forEach(({ responder }) => {
          if (responder) {
            const updated = Helper.getUserObject(responder);
            responders.push({ id: updated.id, label: updated._listname });
          }
        });
        task.responders = responders;
        return {
          ...task,
        };
      });
    }
    if (Array.isArray(risk.comments)) {
      risk.comments = risk.comments.map((comment) => {
        const creator = Helper.getUserObject(comment.creator);
        return {
          ...comment,
          creator: creator,
        };
      });
    }
    risk.document_history = [];
    if (risk.status.toLowerCase() === "accepted" && risk.accepter) {
      risk.document_history.push({
        action: "Approval",
        version: "103",
        name: risk.accepter.name,
        designation: risk.accepter.designation,
        date: new DateTimeUtil(risk.accepted_date).toDateFullYearString(),
      });
    }
    if (
      (risk.status.toLowerCase() === "reviewed" ||
        risk.status.toLowerCase() === "accepted") &&
      risk.reviewer
    ) {
      risk.document_history.push({
        action: "Review",
        version: "102",
        name: risk.reviewer.name,
        designation: risk.reviewer.designation,
        date: new DateTimeUtil(risk.reviewed_date).toDateFullYearString(),
      });
    }
    if (risk.creator) {
      risk.document_history.push({
        action: "Draft",
        version: "101",
        name: risk.creator.name,
        designation: risk.creator.designation,
        date: new DateTimeUtil(risk.created_at).toDateFullYearString(),
      });
    }
    risk.attachments = risk.attachments || [];
  }
  return risk;
};

export function convertToCSV(headers, rows) {
  let csv = headers.join(",") + "\n";
  rows.forEach((row) => {
    csv += headers.map((h) => JSON.stringify(row[h] || "")).join(",") + "\n";
  });
  return csv;
}

export function* handleFileImport({ apiPath, file, fetchAction }) {
  const formData = new FormData();
  formData.append("file", file);

  const res = yield NMAxiosInst.post(
    apiPath,
    formData,
    NMAxiosInst.getFormDataContentType(),
  );

  const data = res;

  const csv = convertToCSV(data.headers, data.rows);
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);

  const popupResult = {
    success: data.success,
    failure: data.failure,
    filename: data.filename,
    message: data.message,

    onDownloadResult: () => {
      const a = document.createElement("a");
      a.href = url;
      a.download = data.filename;
      a.click();
    },
  };
  yield put(riskActions.setUploadRisksResult(popupResult));
  if (fetchAction) {
    yield put(fetchAction());
  }
}

function* fetchThreats() {
  yield requestHandler(function* () {
    const res = yield NMAxiosInst.get("/risks/index_threats");
    const transformed = res
      .map((content) => ({
        ...content,
        source: content.source?.name,
        id: content.id,
        label: content.threat,
      }))
      .filter((threat) => threat.status === "active");
    const sorted = transformed
      .slice()
      .sort(DateTimeUtil.sorterCB.bind(null, "created_at"));
    yield put(riskActions.setThreatsList(sorted));
  }, "Failed to fetch threats");
}

function* fetchVulnerabilities() {
  yield requestHandler(function* () {
    const res = yield NMAxiosInst.get("/risks/index_vulnerabilities");
    const transformed = res
      .map((content) => ({
        ...content,
        threat: content.threat?.threat,
        id: content.id,
        label: content.vulnerability,
      }))
      .filter((vulnerability) => vulnerability.status === "active");
    const sorted = transformed
      .slice()
      .sort(DateTimeUtil.sorterCB.bind(null, "created_at"));
    yield put(riskActions.setVulnerabilitiesList(sorted));
  }, "Failed to fetch Vulnerabilities");
}

function* fetchAssetList() {
  yield requestHandler(function* () {
    let res = yield NMAxiosInst.get("/risks/index_assets");
    const transformed = res.map((content) => ({
      ...content,
      asset_type: content?.asset_type?.asset_type ?? "",
      id: content.id,
      label: content.asset,
    }));
    const sorted = transformed
      .slice()
      .sort(DateTimeUtil.sorterCB.bind(null, "created_at"));
    yield put(riskActions.setAssetsList(sorted));
  }, "Failed to fetch Assets");
}

function* fetchAssetTypeList() {
  yield requestHandler(function* () {
    const res = yield NMAxiosInst.get("/risks/asset_types_index");
    const list = Array.isArray(res) ? res : [];
    const sorted = list
      .slice()
      .sort(DateTimeUtil.sorterCB.bind(null, "created_at"));
    yield put(riskActions.setAssetTypesList(sorted));
  }, "Failed to fetch Assets");
}

function* fetchReferences() {
  yield requestHandler(function* () {
    const res = yield NMAxiosInst.get("/risks/references_index");
    const transformed = res
      .map((content) => ({
        ...content,
        id: content?.id,
        label: content?.reference,
      }))
      .filter((asset) => asset.status === "active");
    const sorted = transformed
      .slice()
      .sort(DateTimeUtil.sorterCB.bind(null, "created_at"));
    yield put(riskActions.setReferencesList(sorted));
  }, "Failed to fetch References");
}
const RATING_TYPES = [
  "confidentiality",
  "integrity",
  "availability",
  "security",
  "privacy",
  "impact",
  "acceptance",
];

const groupRatingsByType = (list = []) => {
  const grouped = RATING_TYPES.reduce((acc, k) => {
    acc[k] = [];
    return acc;
  }, {});

  // filter out wrong-seeded rows (rating null/empty)
  const cleanList = (list || []).filter(
    (item) =>
      item &&
      item.rating_type &&
      item.rating != null && // removes null/undefined
      String(item.rating).trim() !== "", // removes ""
  );

  for (const item of cleanList) {
    const key = String(item.rating_type || "").toLowerCase();
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(item);
  }

  for (const key of Object.keys(grouped)) {
    grouped[key].sort((a, b) => (b?.score ?? 0) - (a?.score ?? 0));
  }

  return grouped;
};

function* fetchRatings() {
  yield requestHandler(function* () {
    const res = yield NMAxiosInst.get("/risks/ratings");
    const grouped = groupRatingsByType(res);
    yield put(riskActions.setRatingsList(grouped));
  }, "Failed to fetch Ratings");
}

function* fetchDashboardData() {
  yield requestHandler(function* () {
    const res = yield NMAxiosInst.get("/risks/org_risks_dashboard");
    yield put(riskActions.setDashboardData(res));
  }, "Failed to fetch stats data");
}

function* createRisk(action) {
  const options = {
    errorMessage: "Failed to create Risk",
    action: action,
  };
  yield requestHandler(function* () {
    const body = action.payload;
    yield NMAxiosInst.post("/risks/org_risk_create", { org_risk: body });
    yield put(riskActions.fetchRiskList());
    yield setAlertAndLoading(null, { message: "Risk created successfully." });
  }, options);
}

function* fetchRiskDeptResponders() {
  const options = { errorMessage: "Failed to Fetch Department Responders" };
  yield requestHandler(function* () {
    let res = yield NMAxiosInst.get("/risks/risk_dept_responders");
    let list = res.dept_responders ? res.dept_responders : res;
    list = Array.isArray(list) ? list : [];
    list = list.map((item) => {
      item.responder_name = "";
      if (item.responder) {
        item.responder = Helper.getUserObject(item.responder);
      }
      return { ...item };
    });
    yield put(riskActions.setDeptResponders(list));
  }, options);
}

function* fetchSources() {
  yield requestHandler(function* () {
    let res = yield NMAxiosInst.get("/risks/sources_index");
    res = res.filter((source) => source.status === "active");
    yield put(riskActions.setSourcesList(res));
  }, "Failed to fetch Sources");
}

function* updateRiskDeptResponder(action) {
  const options = {
    errorMessage: "Failed to Update Department Responder",
    action,
  };
  yield requestHandler(function* () {
    yield setAlertAndLoading(false);
    const { departmentId, userId } = action.payload;
    const body = {
      risk_dept_responders: {
        department_name: departmentId,
        responder_id: userId,
      },
    };
    yield NMAxiosInst.post("/risks/set_risk_dept_responder", body);
    yield setAlertAndLoading(null, {
      message: "Responder updated successfully.",
    });
    yield put(riskActions.fetchRiskDeptResponders());
  }, options);
}
function* removeRiskDeptResponder(action) {
  const options = {
    errorMessage: "Failed to Remove Department Responder",
    action,
  };
  yield requestHandler(function* () {
    yield setAlertAndLoading(false);
    const { departmentId } = action.payload;
    const body = {
      risk_dept_responders: {
        department_name: departmentId,
      },
    };
    yield NMAxiosInst.post("/risks/remove_risk_dept_responder", body);
    yield setAlertAndLoading(null, {
      message: "Responder removed successfully.",
    });
    yield put(riskActions.fetchRiskDeptResponders());
  }, options);
}

function* fetchRiskList({ payload }) {
  yield requestHandler(function* () {
    const pageNo = payload && payload.pageNo ? payload.pageNo : 1;
    const pageSize = payload && payload.pageSize ? payload.pageSize : PER_PAGE;
    const filter = payload && payload.filter ? payload.filter : {};
    const { risks, total_count, org_risks_count } = yield NMAxiosInst.post(
      `/risks/org_risk_list?page=${pageNo}&per_page=${pageSize}`,
      filter,
    );
    const severityList = yield select(getSeverityList);
    const decisionList = yield select(getDecisionList);
    const riskStatusList = yield select(getRiskStatusList);
    const mappedRisks = risks.map((risk) =>
      mapRisks(risk, {
        severityList,
        decisionList,
        riskStatusList,
        doPreproccess: false,
      }),
    );
    yield put(
      riskActions.setRiskList({
        risks: mappedRisks,
        total_count: total_count,
        org_risks_count,
      }),
    );
  }, "Failed to fetch Risk List");
}

function* importRisksFromBlueprint() {
  const options = { errorMessage: "Failed to import risks" };
  yield requestHandler(function* () {
    yield NMAxiosInst.post("/risks/import_from_risk_blueprint", {
      risk_id: "all",
    });
    yield put(riskActions.fetchRiskList());
  }, options);
}
function* uploadRisks(action) {
  const options = {
    errorMessage: "Failed to upload risk",
    action,
  };
  yield requestHandler(function* () {
    const { file } = action.payload;
    yield* handleFileImport({
      apiPath: "/risks/upload_org_risks",
      file,
      fetchAction: riskActions.fetchRiskList,
    });
  }, options);
}
function* fetchRisk({ payload }) {
  yield requestHandler(function* () {
    const { riskId } = payload;
    const res = yield NMAxiosInst.get(`/risks/${riskId}/org_risk_show`);
    const decisionList = yield select(getDecisionList);
    const severityList = yield select(getSeverityList);
    const priorities = yield select(getPriorities);
    const statuses = yield select(getStatuses);
    const riskStatusList = yield select(getRiskStatusList);
    const mappedRisk = mapRisks(res, {
      severityList,
      decisionList,
      priorities,
      statuses,
      riskStatusList,
      doPreproccess: true,
    });
    try {
      mappedRisk.risk_controls = yield NMAxiosInst.get(
        `/risks/${riskId}/org_risk_controls_index`,
      );
    } catch (error) {
      mappedRisk.risk_controls = [];
    }
    yield put(riskActions.setCurrentRisk(mappedRisk));
    return mappedRisk;
  }, "Failed to fetch the risk");
}

function* addComments(comment) {
  const currentRisk = yield select(getCurrentRisk);
  if (!Array.isArray(comment)) {
    comment = [comment];
  }
  for (const commentItem of comment) {
    const formData = new FormData();
    formData.append("comment[comment]", commentItem.comment);
    formData.append("org_risk_id", currentRisk.id);
    if (commentItem.is_eval_comment) {
      formData.append("is_eval_comment", true);
    }
    yield NMAxiosInst.post(
      `/risks/comment`,
      formData,
      NMAxiosInst.getFormDataContentType(),
    );
  }
}

function* updateRisk(action) {
  const options = {
    errorMessage: "Failed to update Risk",
    action: action,
  };

  yield requestHandler(function* () {
    const { risk, comment } = action.payload;
    const currentRisk = yield select(getCurrentRisk);
    const riskStatusList = yield select(getRiskStatusList);

    // If marking as reviewed, also set risk_status to "reviewed" to trigger backend email
    if (risk?.status === "reviewed" && !risk?.risk_status) {
      risk.risk_status = "reviewed";
    }

    // If sending accepter_id, also set approver_id to trigger backend acceptance email
    if (risk?.accepter_id && !risk?.approver_id) {
      risk.approver_id = risk.accepter_id;
    }

    // ---- existing logic unchanged ----
    if (risk.risk_status && isRiskCompleted(riskStatusList, risk.risk_status)) {
      risk.status = "accepted";
    } else if (
      risk.risk_status &&
      currentRisk.status === "accepted" &&
      !isRiskCompleted(riskStatusList, risk.risk_status)
    ) {
      risk.status = "draft";
      risk.reviewed_date = null;
      risk.reviewer_id = null;
      risk.accepter_id = null;
      risk.accepted_date = null;
    }

    const res = yield NMAxiosInst.put(
      `/risks/${currentRisk.id}/org_risk_update`,
      {
        org_risk: risk,
      },
    );

    if (comment) {
      yield* addComments(comment);
    }
    yield put(riskActions.fetchRisk({ riskId: currentRisk.id }));
    yield setAlertAndLoading(null, { message: "Risk updated successfully." });
    return res;
  }, options);
}

function* addRiskControl(action) {
  const options = {
    errorMessage: "Failed to add Risk Control",
    action: action,
  };
  yield requestHandler(function* () {
    const { control, comment } = action.payload;
    const currentRisk = yield select(getCurrentRisk);
    yield NMAxiosInst.post(`/risks/create_org_risk_control`, {
      org_risk_control: control,
      id: currentRisk.id,
    });
    if (comment) {
      yield* addComments(comment);
    }
    const res = yield put(riskActions.fetchRisk({ riskId: currentRisk.guid }));
    yield setAlertAndLoading(null, {
      message: "Risk Control added successfully.",
    });
    return res;
  }, options);
}

function* deleteRiskControl(action) {
  const options = {
    errorMessage: "Failed to delete Risk Control",
    action: action,
  };
  yield requestHandler(function* () {
    const { controlId, comment } = action.payload;
    const currentRisk = yield select(getCurrentRisk);
    yield NMAxiosInst.delete(`/risks/${controlId}/destroy_org_risk_control`);
    if (comment) {
      yield* addComments(comment);
    }
    const res = yield put(riskActions.fetchRisk({ riskId: currentRisk.guid }));
    yield setAlertAndLoading(null, {
      message: "Risk Control deleted successfully.",
    });
    return res;
  }, options);
}

function* addRiskResponder(action) {
  const options = {
    errorMessage: "Failed to add Risk Responder",
    action: action,
  };
  yield requestHandler(function* () {
    const { responderId, comment } = action.payload;
    const currentRisk = yield select(getCurrentRisk);
    const body = {
      responder: {
        ent_usr_id: responderId,
      },
      org_risk_id: currentRisk.id,
    };
    yield NMAxiosInst.post(`/risks/add_org_risk_responder`, body);
    if (comment) {
      yield* addComments(comment);
    }
    const res = yield put(riskActions.fetchRisk({ riskId: currentRisk.id }));
    yield setAlertAndLoading(null, {
      message: "Risk Responder added successfully.",
    });
    return res;
  }, options);
}

function* removeRiskResponder(action) {
  const options = {
    errorMessage: "Failed to remove Risk Responder",
    action: action,
  };
  yield requestHandler(function* () {
    const { responderId, comment } = action.payload;
    const currentRisk = yield select(getCurrentRisk);
    const body = {
      responder: {
        ent_usr_id: responderId,
      },
      org_risk_id: currentRisk.id,
    };
    yield NMAxiosInst.post(`/risks/remove_org_risk_responder`, body);
    if (comment) {
      yield* addComments(comment);
    }
    const res = yield put(riskActions.fetchRisk({ riskId: currentRisk.id }));
    yield setAlertAndLoading(null, {
      message: "Risk Responder removed successfully.",
    });
    return res;
  }, options);
}

function* createRiskTask(action) {
  const options = {
    errorMessage: "Failed to create Risk Task",
    action: action,
  };
  yield requestHandler(function* () {
    const { task } = action.payload;
    const currentRisk = yield select(getCurrentRisk);
    const body = {
      task: task,
      org_risk_id: currentRisk.id,
    };
    const res = yield NMAxiosInst.post(`/risks/create_task`, body);
    yield put(riskActions.fetchRisk({ riskId: currentRisk.id }));
    const tasks = Array.isArray(res.tasks) ? res.tasks : [];
    const taskId = tasks.length > 0 ? tasks[tasks.length - 1].task_id : null;
    if (taskId) {
      const comment = {
        comment: `Created Task <span class="fw-600">${taskId}</span>.`,
      };
      yield* addComments(comment);
    }
    yield setAlertAndLoading(null, {
      message: "Risk Task created successfully.",
    });
    return res;
  }, options);
}

function* fetchImportRisksCount() {
  yield requestHandler(function* () {
    let res = yield NMAxiosInst.get(
      "/risks/import_from_risk_blueprint_count?risk_id=all",
    );
    yield put(riskActions.setImportRisksCount(res?.total_to_create ?? 0));
  }, "Failed to fetch Risks Count");
}

function* updateRiskDeptResponderWithNewUser(action) {
  const options = {
    errorMessage: "Failed to update Risk Department Responder with new user",
    action: action,
  };
  yield requestHandler(function* () {
    const { payload } = action;
    const { ent_usr, department } = payload;
    const body = {
      ent_usr: ent_usr,
    };
    const result = yield NMAxiosInst.post(`/consoles/create_org_user`, body);
    if (result.user) {
      yield put(commonAction.fetchActiveUsers({ orgId: ent_usr.ent_org_id }));
      yield put(
        riskActions.updateRiskDeptResponder({
          departmentId: department,
          userId: result.user.id,
        }),
      );
    }
  }, options);
}

function* exportRiskReport(action) {
  const options = {
    errorMessage: "Failed to export Risk Report",
    action: action,
  };
  yield requestHandler(function* () {
    const currentRisk = yield select(getCurrentRisk);
    const { /*password*/ } = action.payload;
    const res = yield NMAxiosInst.postExport(`/risks/export_risk_report`, {
      org_risk_id: currentRisk.id,
      // password: password,
    });
    if (res && res.error) {
      yield setAlertAndLoading(null, {
        message: "Risk Report Download Failed",
        success: false,
      });
    } else {
      yield setAlertAndLoading(null, {
        message: "Risk Report downloaded successfully",
      });
      saveAs(res, `Risk_Acceptance_Record_${currentRisk.module_risk_id}.pdf`);
    }
  }, options);
}

function* shareRiskReport(action) {
  const options = {
    errorMessage: "Failed to share Risk Report",
    action: action,
  };
  yield requestHandler(function* () {
    const currentRisk = yield select(getCurrentRisk);
    const { /*password,*/ emailAddress } = action.payload;
    const body = {
      email: emailAddress,
      // password: password,
      risk_id: currentRisk.id,
    };
    yield NMAxiosInst.post(`/risks/share_risk_report`, body);
    yield setAlertAndLoading(null, {
      message: "Risk Report shared successfully.",
    });
  }, options);
}

function downloadCsvText(csvText, fileName = "Org_Risks.csv") {
  const withBOM = "\uFEFF" + (csvText || ""); // Excel-friendly UTF-8
  const blob = new Blob([withBOM], { type: "text/csv;charset=utf-8;" });
  const url = window.URL.createObjectURL(blob);

  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();

  a.remove();
  window.URL.revokeObjectURL(url);
}

function* exportOrgRisks(action) {
  try {
    yield requestHandler(function* () {
      const res = yield NMAxiosInst.get("/risks/export_org_risks", {
        responseType: "text",
        headers: { Accept: "text/csv" },
      });

      const csvText = typeof res === "string" ? res : res?.data;
      if (!csvText) throw new Error("Empty export response");

      downloadCsvText(csvText, "Org_Risks.csv");
    }, "Failed to export Org Risks.");

    action?.onComplete?.(); // close popup
  } catch (e) {
    action?.onFailure?.(e);
    throw e;
  }
}

function* createRiskReport(action) {
  const options = {
    errorMessage: "Failed to create Risk Report",
    action: action,
  };
  yield requestHandler(function* () {
    const currentRisk = yield select(getCurrentRisk);
    yield NMAxiosInst.post(`/risks/create_risk_report`, {
      org_risk_id: currentRisk.id,
    });
    yield put(riskActions.fetchRisk({ riskId: currentRisk.id }));
  }, options);
}

function* fetchRiskReport(action) {
  const options = {
    errorMessage: "Failed to fetch Risk Report",
    action: action,
  };
  yield requestHandler(function* () {
    const currentRisk = yield select(getCurrentRisk);
    const { url } = action.payload;
    const report = yield NMAxiosInst.postExport(
      "/incidents/s3_download_decrypt_file",
      { url: url },
    );
    yield put(riskActions.setCurrentRisk({ ...currentRisk, report: report }));
  }, options);
}

SagaRegistry.register(function* riskSaga() {
  yield takeEvery("risk/fetchThreats", fetchThreats);
  yield takeEvery("risk/fetchVulnerabilities", fetchVulnerabilities);
  yield takeEvery("risk/fetchAssetList", fetchAssetList);
  yield takeEvery("risk/fetchAssetTypeList", fetchAssetTypeList);
  yield takeEvery("risk/fetchReferences", fetchReferences);
  yield takeEvery("risk/fetchSources", fetchSources);
  yield takeEvery("risk/fetchRatings", fetchRatings);
  yield takeEvery("risk/fetchDashboardData", fetchDashboardData);
  yield takeEvery("risk/createRisk", createRisk);
  yield takeEvery("risk/fetchRiskDeptResponders", fetchRiskDeptResponders);
  yield takeEvery("risk/updateRiskDeptResponder", updateRiskDeptResponder);
  yield takeEvery("risk/removeRiskDeptResponder", removeRiskDeptResponder);
  yield takeEvery("risk/fetchRiskList", fetchRiskList);
  yield takeEvery("risk/importRisksFromBlueprint", importRisksFromBlueprint);
  yield takeEvery("risk/uploadRisks", uploadRisks);
  yield takeEvery("risk/fetchRisk", fetchRisk);
  yield takeEvery("risk/updateRisk", updateRisk);
  yield takeEvery("risk/addRiskControl", addRiskControl);
  yield takeEvery("risk/deleteRiskControl", deleteRiskControl);
  yield takeEvery("risk/addRiskResponder", addRiskResponder);
  yield takeEvery("risk/removeRiskResponder", removeRiskResponder);
  yield takeEvery("risk/createRiskTask", createRiskTask);
  yield takeEvery("risk/fetchImportRisksCount", fetchImportRisksCount);
  yield takeEvery(
    "risk/updateRiskDeptResponderWithNewUser",
    updateRiskDeptResponderWithNewUser,
  );
  yield takeEvery("risk/exportRiskReport", exportRiskReport);
  yield takeEvery("risk/exportOrgRisks", exportOrgRisks);
  yield takeEvery("risk/shareRiskReport", shareRiskReport);
  yield takeEvery("risk/createRiskReport", createRiskReport);
  yield takeEvery("risk/fetchRiskReport", fetchRiskReport);
});
